import {
  NodoDto,
  ProprietaMisurataDto,
  ProprietaMisurataValueDto,
  useGetRegistriDispositivo,
} from "@/api";
import { Box, Card, Typography } from "@mui/material";
import HeaderNovus from "../../../../../assets/header_novus.png";
import moment from "moment";
import { useMemo } from "react";
import { getMascheraDispositivoHelper } from "@/components/DispositivoDetails/maschere";
import { getDispositivoColor } from "@/utils/dispositivoUtils";

export type NovusCardProps = {
  dispositivo: NodoDto;
  proprietaMisurata: ProprietaMisurataDto;
  proprietaMisurateFft?: ProprietaMisurataDto[];
  value: ProprietaMisurataValueDto | null;
  oldValue: boolean;
};

export default function NovusCard({
  dispositivo,
  proprietaMisurata,
  proprietaMisurateFft,
  value,
  oldValue,
}: NovusCardProps) {
  const helper = dispositivo?.dispositivoModello?.maschera
    ? getMascheraDispositivoHelper(dispositivo.dispositivoModello.maschera)
    : null;
  const { data: registriData } = useGetRegistriDispositivo(dispositivo.id, {
    query: { enabled: proprietaMisurata.type.influxDbTag === "novaDiagnostic" },
  });
  const registriValuesMap = useMemo(() => {
    if (!registriData || !helper || !("registriToForm" in helper)) {
      return null;
    }

    return helper.registriToForm(registriData.data);
  }, [helper, registriData]);

  const novusData = useMemo(() => {
    if (typeof value?.value !== "number") {
      return null;
    }

    if (proprietaMisurata.type.influxDbTag === "trendTemp") {
      switch (value.value) {
        case 0:
          return {
            // verde
            color: "#00bf63",
            text: "Temperatura costante",
          };
        case 1:
          // arancio
          return { color: "#ffde59", text: "Temperatura in aumento" };
        case 2:
          return { color: "#4277A0", text: "Temperatura in diminuzione" };
        default:
          return null;
      }
    } else if (proprietaMisurata.type.influxDbTag === "rms_trend") {
      switch (value.value) {
        case 0:
          return {
            // verde
            color: "#00bf63",
            text: "Vibrazione costante",
          };
        case 1:
          return {
            // arancio
            color: "#ffde59",
            text: "Vibrazione in aumento",
          };
        case 2:
          return {
            color: "#4277A0",
            text: "Vibrazione in diminuzione",
          };
        default:
          return null;
      }
    } else if (proprietaMisurata.type.influxDbTag === "novaDiagnostic") {
      const textAlarms: string[] = [];

      if (proprietaMisurateFft) {
        for (const proprieta of proprietaMisurateFft) {
          const armonicaIndex = parseInt(
            proprieta.type.influxDbTag.replace("fft", "")
          );
          if (armonicaIndex) {
            const soglia1Value = parseFloat(
              registriValuesMap?.[`fft_f${armonicaIndex}_soglia_1`]
            );
            if (
              proprieta.lastValue?.value &&
              proprieta.lastValue?.value > soglia1Value
            ) {
              textAlarms.push(
                registriValuesMap?.[`fft_f${armonicaIndex}_guasto`] ??
                  "Registro del guasto non configurato"
              );
            }
          }
        }
      }

      const text =
        textAlarms.length === 0 ? "Condizioni ottimali" : textAlarms.join(", ");

      if (value.value) {
        const c = getDispositivoColor(dispositivo, {
          sourceNovusDiagnostic: true,
        });
        if (c) {
          return {
            color: c.color,
            text: text,
          };
        }
      }

      return { color: "gray", text: "Condizione sconosciuta" };
    } else {
      return null;
    }
  }, [
    dispositivo,
    value,
    registriValuesMap,
    proprietaMisurateFft,
    proprietaMisurata.type.influxDbTag,
  ]);

  return (
    <Card
      sx={{
        minWidth: 320,
        maxWidth: 320,
        minHeight: 320,
        maxHeight: 320,
        backgroundColor: "#e4f4f3",
      }}
    >
      <Box
        p={2}
        pt={0}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flex={1}
      >
        <img src={HeaderNovus} width="100%" alt="Novus" />

        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: novusData?.color,
            width: "100%",
            marginTop: "10px",
            borderRadius: "10px",
            textAlign: "center",
          }}
        >
          <Typography
            style={{ color: novusData ? "white" : undefined }}
            fontWeight="bold"
          >
            {novusData?.text}
          </Typography>
        </Box>

        {oldValue && (
          <Box>
            <Typography variant="caption" fontWeight="bold" color="warning">
              {value
                ? `Ultimo rilevamento: ${moment(value.timestamp).format(
                    "DD/MM/YYYY HH:mm:ss"
                  )}`
                : "Dato non rilevato"}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
}
